/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useState } from "react";
import { Else, If, Then } from "react-if";
import {
  ResourceFragment,
  ResourcesSortFields,
  SortOrder,
  CloudAccountCloudProvider,
  OsType,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { Pagination } from "~/components/Pagination";
import {
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput";
import { TableWrapper, SelectionBanner } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { useTableRowMultiPageSelection } from "~/hooks/useTableRowMultiPageSelection";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import {
  useResourceComplianceStatusForUI,
  useGetResourceUpdateStatus,
  useGetAllResourceDetails,
} from "~/views/resources/hooks";
import { useResourcesSearch } from "~/views/resources/hooks/useResourcesSearch/useResourcesSearch";
import { ResourceQuery } from "~/views/resources/types";
import { ResourceCtxProvider } from "../ResourceProvider";
import { prepareFilter } from "../utils";
import { ResourceListHeader } from "./ResourceListHeader";
import { columns } from "./ResourcesColumns";

type Props = {
  provider?: CloudAccountCloudProvider;
  providerId?: string;
  showAccountColumn?: boolean;
};

type SelectedResourceDetails = {
  id: string;
  name: string;
  scanned: boolean;
  os?: OsType | null;
};

export function ResourcesTable({
  provider,
  providerId,
  showAccountColumn,
}: Props) {
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();
  const [fetchedResources, setFetchedResources] = useState<ResourceFragment[]>(
    []
  );

  useEffect(() => {
    if (
      providerId &&
      !queryState.providerIds?.map((id) => String(id)).includes(providerId)
    ) {
      updateQueryState(
        {
          ...queryState,
          providerIds: [providerId],
        },
        "replace"
      );
    }
  }, [providerId, queryState, updateQueryState]);

  const {
    getAllResourceDetails,
    allResourceDetails,
    loading: allResourceDetailsLoading,
  } = useGetAllResourceDetails({
    input: {
      phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
        ? queryState.searchPhrase
        : undefined,
      filter: prepareFilter(queryState),
    },
    sort: {
      field: queryState.field ?? ResourcesSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const {
    data,
    loading: resourcesLoading,
    count,
  } = useResourcesSearch({
    page: queryState.page,
    limit: queryState.limit,
    includeAccount: showAccountColumn ?? !providerId,
    includeGroupIdentifiers: true,
    includeFuturePatchingActions: true,
    input: {
      phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
        ? queryState.searchPhrase
        : undefined,
      filter: prepareFilter(queryState),
    },
    sort: {
      field: queryState.field ?? ResourcesSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  const resources = useMemo(() => data.filter(isNotNil) ?? [], [data]);
  const resourceIds = useMemo(
    () => resources.map((resource) => resource.id),
    [resources]
  );

  const resourceIdsKey = resourceIds.join(",");

  useEffect(() => {
    setFetchedResources((prev) => {
      const fetchedResourceIds = new Set(prev.map((resource) => resource.id));

      const newResources = resources.filter(
        (resource) => !fetchedResourceIds.has(resource.id)
      );

      return [...prev, ...newResources];
    });
    // Use stable resourceIdsKey instead of resources or resourceIds to avoid an infinite loop from object reference changes.
  }, [resourceIdsKey]);

  const {
    rowSelection,
    setRowSelection,
    selectedItemIds: selectedResourceIds,
    setSelectedItemIds: setResourcesIds,
    selectedItemCountAcrossPages,
    areAllItemsSelected: allResourcesSelected,
    clearSelection,
    selectAllItems,
  } = useTableRowMultiPageSelection({
    currentPageIds: resourceIds,
    totalCount: count ?? 0,
  });

  // Clear selection when filters/search change, except page
  const selectionResetKey = useMemo(
    () => JSON.stringify({ ...queryState, page: undefined }),
    [queryState]
  );

  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionResetKey]);

  const selectedResourcesDetails = useMemo<SelectedResourceDetails[]>(() => {
    if (allResourcesSelected) {
      return allResourceDetails;
    }

    const selectedIdsSet = new Set(selectedResourceIds);

    if (allResourceDetails.length === 0) {
      return fetchedResources
        .filter((resource) => selectedIdsSet.has(resource.id))
        .map(mapResourceToSelectedResourceDetails);
    }

    return allResourceDetails
      .filter((resourceDetail) => selectedIdsSet.has(resourceDetail.id))
      .map((resourceDetail) => resourceDetail);
  }, [
    allResourcesSelected,
    selectedResourceIds,
    fetchedResources,
    allResourceDetails,
  ]);

  const { data: updateStatuses, loading: updateStatusesLoading } =
    useGetResourceUpdateStatus({
      resourceIds,
      skip: resourcesLoading,
    });

  const { data: complianceStatuses, loading: complianceStatusesLoading } =
    useResourceComplianceStatusForUI({
      resourceIds,
      skip: resourcesLoading,
    });

  const isLoading = resourcesLoading;

  return (
    <ResourceCtxProvider value={{ cleanRowSelection: clearSelection }}>
      <ResourceListHeader
        resourcesDetails={selectedResourcesDetails}
        selectedResourceIds={selectedResourceIds}
        allResourcesSelected={allResourcesSelected}
        hasResources={resources.length > 0}
        provider={provider}
        resultsCount={count ?? 0}
      />
      <SelectionBanner
        itemName="Resource"
        totalItems={count}
        selectedItemCount={selectedItemCountAcrossPages}
        areAllItemsSelected={allResourcesSelected}
        clearItemsSelection={clearSelection}
        areAllItemsLoading={allResourceDetailsLoading}
        selectAllItems={async () => {
          const queryResult = await getAllResourceDetails();
          const allItemIds =
            queryResult.data?.resourcesSearch?.result
              ?.filter(
                (result): result is NonNullable<typeof result> => !!result?.id
              )
              ?.map((result) => result.id) ?? [];
          selectAllItems(allItemIds);
        }}
        showSelectAllItems={queryState.limit <= count}
      />
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={isLoading} inContent>
            <If condition={isEmpty(resources) && !isLoading}>
              <Then>
                <NoData hasIcon message={"There are no Resources."} />
              </Then>
              <Else>
                <TableWrapper<ResourceFragment>
                  loading={isLoading}
                  data={resources}
                  columns={columns({
                    showAccountColumn: showAccountColumn ?? !providerId,
                    updateStatuses,
                    updateStatusesLoading,
                    complianceStatuses,
                    complianceStatusesLoading,
                  })}
                  selectedRowsHandler={setResourcesIds}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  stripped
                />
                <Pagination count={count} />
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </ResourceCtxProvider>
  );
}

function mapResourceToSelectedResourceDetails(resource: ResourceFragment) {
  return {
    id: resource.id,
    name: resource.name,
    scanned: resource.scanned,
    os: resource.os?.type ?? null,
  };
}
